/**
 * Unresolved discovery clues — V109-2-2 / ADR-0088 §1 (migration 655).
 *
 *   - What a scan or a collector found but could not resolve to a product in the provider
 *     registry is STORED (it used to be counted only): the raw identifier, where it came from,
 *     which run. Nothing is guessed.
 *   - A person resolves a clue to a product (the item is created or found, with a source from
 *     the clue's origin) or dismisses it with a reason. Later re-reads honour the resolution:
 *     the same identifier lands on the chosen product. A dismissed clue stays dismissed.
 *   - A complete re-read that no longer finds a clue sets `gone_at` (its status is kept).
 *   - Every query filters by the caller's workspace id; another workspace's clue is not_found.
 *     Free text passes the secret / e-mail filter; an identifier that looks like one is not stored.
 */
import { generateRandomId } from '../utils/audit-crypto.js';
import { containsSecretOrEmail } from '../utils/secret-filter.js';
import { assertKnownProduct, recordInventoryClue } from './infra-inventory-service.js';
import { createExternalService, normalizeProviderKey } from './boundary-service.js';
import { ProposedChangeService } from './proposed-change-service.js';
import { PRODUCT_KEY_ALIASES, getProvider } from '../connectors/providers.js';
export const CLUE_SOURCE_KINDS = ['repo_scan', 'connector_resource', 'sbom_component', 'oauth_app', 'connector_scope', 'gcp_asset_inventory', 'cloudflare_collector'];
export const CLUE_KINDS = ['cloud_resource_type', 'sdk', 'oauth_app', 'resource_type'];
export class UnresolvedClueError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'UnresolvedClueError';
    }
}
const fail = (code, m) => { throw new UnresolvedClueError(code, m); };
const toClue = ({ detail_json, ...r }) => {
    let detail = {};
    try {
        const v = JSON.parse(detail_json);
        if (v && typeof v === 'object' && !Array.isArray(v))
            detail = v;
    }
    catch { /* {} */ }
    return { ...r, occurrences: Number(r.occurrences), detail };
};
const identityKey = (c) => `${c.source_kind}|${c.ref_id}|${c.clue_kind}|${c.raw_identifier}`;
/**
 * Store one read's clues (upsert; a re-read refreshes `last_seen_at`, never duplicates). With
 * `completeKinds`, every live clue of those source kinds this read did not produce is gone.
 * Identifiers that look like a secret or an e-mail address are skipped (counted in `skipped`).
 */
export async function recordUnresolvedClues(db, workspaceId, clues, opts = {}) {
    const nowIso = (opts.now ?? new Date()).toISOString();
    const newId = opts.newId ?? ((p) => generateRandomId(p));
    const merged = new Map();
    let skipped = 0;
    for (const c of clues) {
        const raw = (c.raw_identifier ?? '').trim().slice(0, 500);
        const ref = (c.ref_id ?? '').trim().slice(0, 500);
        if (!raw || !ref || containsSecretOrEmail(raw) || !CLUE_SOURCE_KINDS.includes(c.source_kind) || !CLUE_KINDS.includes(c.clue_kind)) {
            skipped++;
            continue;
        }
        const x = { ...c, raw_identifier: raw, ref_id: ref, occurrences: Math.max(1, Math.floor(c.occurrences ?? 1)) };
        const prev = merged.get(identityKey(x));
        merged.set(identityKey(x), prev ? { ...x, occurrences: prev.occurrences + x.occurrences } : x);
    }
    const existing = new Map((await db.query(`SELECT * FROM infra_unresolved_clues WHERE workspace_id = ?`, [workspaceId])).results.map((r) => [identityKey(r), r]));
    for (const [key, c] of merged) {
        const detail = JSON.stringify(c.detail ?? {});
        const prior = existing.get(key);
        if (prior) {
            await db.execute(`UPDATE infra_unresolved_clues SET run_id = COALESCE(?, run_id), detail_json = ?, occurrences = ?, last_seen_at = ?, gone_at = NULL, updated_at = ? WHERE clue_id = ? AND workspace_id = ?`, [c.run_id ?? null, detail, c.occurrences, nowIso, nowIso, prior.clue_id, workspaceId]);
        }
        else {
            await db.execute(`INSERT INTO infra_unresolved_clues (clue_id, workspace_id, source_kind, clue_kind, raw_identifier, ref_id, run_id, detail_json, occurrences, first_seen_at, last_seen_at, status, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'open', ?)`, [newId('iclue'), workspaceId, c.source_kind, c.clue_kind, c.raw_identifier, c.ref_id, c.run_id ?? null, detail, c.occurrences, nowIso, nowIso, nowIso]);
        }
    }
    let gone = 0;
    const complete = new Set(opts.completeKinds ?? []);
    for (const [key, r] of existing) {
        if (!complete.has(r.source_kind) || r.gone_at || merged.has(key))
            continue;
        await db.execute(`UPDATE infra_unresolved_clues SET gone_at = ?, updated_at = ? WHERE clue_id = ? AND workspace_id = ? AND gone_at IS NULL`, [nowIso, nowIso, r.clue_id, workspaceId]);
        gone++;
    }
    return { stored: merged.size, skipped, gone };
}
/** What people resolved, by `clue_kind|raw_identifier` → the chosen product (re-reads honour it). */
export async function resolvedClueTargets(db, workspaceId) {
    const rows = (await db.query(`SELECT c.clue_kind, c.raw_identifier, i.provider_key, i.product FROM infra_unresolved_clues c
       JOIN infra_inventory_items i ON i.item_id = c.resolved_item_id AND i.workspace_id = c.workspace_id
      WHERE c.workspace_id = ? AND c.status = 'resolved' ORDER BY c.decided_at ASC`, [workspaceId])).results;
    return new Map(rows.map((r) => [`${r.clue_kind}|${r.raw_identifier}`, { provider_key: r.provider_key, product: r.product }]));
}
export async function getUnresolvedClue(db, workspaceId, clueId) {
    const r = (await db.query(`SELECT * FROM infra_unresolved_clues WHERE clue_id = ? AND workspace_id = ? LIMIT 1`, [clueId, workspaceId])).results[0];
    return r ? toClue(r) : null;
}
/** Clues of the workspace, open first, newest first. Gone clues only with `includeGone`. */
export async function listUnresolvedClues(db, workspaceId, opts = {}) {
    const where = [];
    const params = [workspaceId];
    if (opts.status) {
        where.push('status = ?');
        params.push(opts.status);
    }
    if (!opts.includeGone)
        where.push('gone_at IS NULL');
    return (await db.query(`SELECT * FROM infra_unresolved_clues WHERE workspace_id = ?${where.map((w) => ' AND ' + w).join('')}
      ORDER BY CASE status WHEN 'open' THEN 0 ELSE 1 END, last_seen_at DESC, clue_id ASC LIMIT 1000`, params)).results.map(toClue);
}
/** The inventory source kind a resolved clue's origin becomes. */
const INVENTORY_KIND = {
    repo_scan: 'repo_scan', connector_resource: 'connector_resource', sbom_component: 'sbom_component', oauth_app: 'oauth_app',
    connector_scope: 'connector_resource', gcp_asset_inventory: 'connector_resource', cloudflare_collector: 'connector_resource',
};
async function openClue(db, workspaceId, clueId) {
    const c = await getUnresolvedClue(db, workspaceId, clueId) ?? fail('not_found', 'Clue not found.');
    if (c.status !== 'open')
        fail('conflict', `This clue was already ${c.status}.`);
    return c;
}
const assertPerson = (actor) => { if (!actor || actor.startsWith('system:'))
    fail('invalid', 'A person decides a clue.'); };
/**
 * Resolve: the clue is this product. The item is created (open, for review) or found, with a
 * source from the clue's origin; the clue records the item. Unknown products are refused.
 */
export async function resolveUnresolvedClue(db, input) {
    assertPerson(input.actor_user_id);
    const c = await openClue(db, input.workspace_id, input.clue_id);
    try {
        assertKnownProduct({ provider_key: input.provider_key, product_id: input.product });
    }
    catch (err) {
        fail('invalid', err.message);
    }
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    const itemId = await recordInventoryClue(db, input.workspace_id, {
        provider_key: input.provider_key, product: input.product, source_kind: INVENTORY_KIND[c.source_kind], ref_id: c.ref_id, run_id: null,
        confidence: 0.9, detail: { resolved_from_clue: c.clue_id, clue_kind: c.clue_kind, raw_identifier: c.raw_identifier },
    }, { now, newId: input.newId });
    await db.execute(`UPDATE infra_unresolved_clues SET status = 'resolved', resolved_item_id = ?, decided_by = ?, decided_at = ?, updated_at = ? WHERE clue_id = ? AND workspace_id = ? AND status = 'open'`, [itemId, input.actor_user_id, nowIso, nowIso, c.clue_id, input.workspace_id]);
    return (await getUnresolvedClue(db, input.workspace_id, c.clue_id));
}
/**
 * Reopen a decided clue — V109-5 built-UI #6 (Undo after "Not ours" / "Confirm", and Restore from the
 * "Not ours" row). A dismissed clue goes back to open. A resolved one too: the inventory source its
 * resolution added (detail `resolved_from_clue`) is marked gone, and its item with it when no live
 * source is left (never deleted). Returns the clue and what it was before (for the audit trail).
 */
export async function reopenUnresolvedClue(db, input) {
    assertPerson(input.actor_user_id);
    const c = await getUnresolvedClue(db, input.workspace_id, input.clue_id) ?? fail('not_found', 'Clue not found.');
    if (c.status === 'open')
        fail('conflict', 'This clue is already open.');
    const nowIso = (input.now ?? new Date()).toISOString();
    if (c.status === 'resolved' && c.resolved_item_id) {
        const sources = (await db.query(`SELECT source_id, detail_json FROM infra_inventory_sources WHERE workspace_id = ? AND item_id = ? AND gone_at IS NULL`, [input.workspace_id, c.resolved_item_id])).results;
        const fromClue = sources.filter((s) => {
            try {
                return JSON.parse(s.detail_json || '{}').resolved_from_clue === c.clue_id;
            }
            catch {
                return false;
            }
        });
        for (const s of fromClue) {
            await db.execute(`UPDATE infra_inventory_sources SET gone_at = ? WHERE source_id = ? AND workspace_id = ? AND gone_at IS NULL`, [nowIso, s.source_id, input.workspace_id]);
        }
        if (fromClue.length && fromClue.length === sources.length) {
            await db.execute(`UPDATE infra_inventory_items SET gone_at = ?, updated_at = ? WHERE item_id = ? AND workspace_id = ? AND gone_at IS NULL`, [nowIso, nowIso, c.resolved_item_id, input.workspace_id]);
        }
    }
    // BU-4: Undo of "Other (not listed)…" retires the External service that decision created, when nothing uses it yet.
    if (isProviderPending(c)) {
        const m = PENDING_SERVICE_RE.exec(c.dismiss_reason ?? '');
        if (m?.[1] && m[2]) {
            const used = (await db.query(`SELECT 1 AS ok FROM boundary_components WHERE workspace_id = ? AND removed_at IS NULL AND (external_service_id = ? OR inherited_from_service_id = ?) LIMIT 1`, [input.workspace_id, m[1], m[1]])).results.length > 0;
            if (!used) {
                await db.execute(`UPDATE external_services SET retired_at = ?, retired_by = ?, updated_at = ? WHERE external_service_id = ? AND workspace_id = ? AND retired_at IS NULL`, [nowIso, input.actor_user_id, nowIso, m[1], input.workspace_id]);
            }
        }
    }
    await db.execute(`UPDATE infra_unresolved_clues SET status = 'open', resolved_item_id = NULL, dismiss_reason = NULL, decided_by = NULL, decided_at = NULL, updated_at = ?
      WHERE clue_id = ? AND workspace_id = ? AND status = ?`, [nowIso, c.clue_id, input.workspace_id, c.status]);
    return { clue: (await getUnresolvedClue(db, input.workspace_id, c.clue_id)), before: { status: c.status, resolved_item_id: c.resolved_item_id, dismiss_reason: c.dismiss_reason } };
}
// ---- a provider not in the catalog (V109-5 BU-4) ------------------------------------------------------------
/** A dismissed clue whose reason starts with this was confirmed as used, with its provider pending (never "not ours"). */
export const PROVIDER_PENDING_PREFIX = 'Provider pending: ';
/** `… · external service <id> (new)` — the service this decision created (Undo retires it). */
const PENDING_SERVICE_RE = /external service (xs[A-Za-z0-9_-]*)( \(new\))?/;
export const isProviderPending = (c) => c.status === 'dismissed' && (c.dismiss_reason ?? '').startsWith(PROVIDER_PENDING_PREFIX);
/** The stable review-queue id of the catalog request for a provider key (one per provider, across workspaces). */
export const providerRequestChangeId = (providerKey) => `chg-provider-request-${providerKey}`;
/** A public page about the provider for the request: the website given, the clue's host, or its package page. */
export function providerRequestUrl(c, website) {
    if (website)
        return website;
    const raw = c.raw_identifier.trim().toLowerCase();
    if (/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(raw))
        return `https://${raw}`;
    const m = /^(npm|pypi):(.+)$/.exec(raw);
    const pkg = (m ? m[2] : raw).replace(/[^a-z0-9@/._-]/g, '');
    return m?.[1] === 'pypi' ? `https://pypi.org/project/${encodeURIComponent(pkg)}/` : `https://www.npmjs.com/package/${pkg.split('/').map(encodeURIComponent).join('/')}`;
}
/**
 * "Other (not listed)…" — the clue IS used, but its provider is not in GAC Pro's provider catalog (a
 * reviewed code registry, ADR-0051 §3). Never a false "not ours":
 *   1. An External service for the provider is saved (or the live one reused), provider pending — the
 *      boundary documents it as a third party until the catalog has it.
 *   2. A catalog request goes into the ONE review queue (ADR-0002 / ADR-0006): a `research_source`
 *      create proposal (kind `vendor_catalog`, on demand) for the provider's public page, keyed per
 *      provider so many workspaces asking make one request; a rejection blocklists the page as usual.
 *      It carries the provider's name and public page only — nothing about the workspace's code.
 *   3. The clue leaves the "to confirm" list as dismissed with a `Provider pending: …` reason (an
 *      inventory item needs a catalog product, so it cannot be "resolved" yet); Undo (reopen) retires
 *      the service this decision created when nothing uses it.
 */
export async function resolveClueAsUnlistedProvider(db, input) {
    assertPerson(input.actor_user_id);
    const name = (input.name ?? '').trim();
    if (!name || name.length > 120)
        fail('invalid', 'Name the provider (at most 120 characters).');
    if (containsSecretOrEmail(name))
        fail('invalid', 'The name looks like it contains a secret or an e-mail address; type the company name.');
    const key = normalizeProviderKey(name);
    if (!key)
        fail('invalid', 'The name needs letters or digits.');
    if (getProvider(key) || PRODUCT_KEY_ALIASES[key])
        fail('invalid', `${getProvider(key)?.name ?? name} is in the provider list; choose it there.`);
    let website = null;
    if (input.website != null && input.website.trim()) {
        const w = input.website.trim();
        let u = null;
        try {
            u = new URL(w);
        }
        catch { /* invalid */ }
        if (!u || u.protocol !== 'https:' || w.length > 500 || u.username || u.password || containsSecretOrEmail(w))
            fail('invalid', 'The website must be an https:// address (at most 500 characters).');
        website = u.toString();
    }
    const c = await openClue(db, input.workspace_id, input.clue_id);
    const now = input.now ?? new Date();
    const live = (await db.query(`SELECT external_service_id FROM external_services WHERE workspace_id = ? AND provider_key = ? AND retired_at IS NULL AND COALESCE(access_channel, '') = '' AND COALESCE(product, '') = '' LIMIT 1`, [input.workspace_id, key])).results[0];
    const xs = live ?? await createExternalService(db, {
        workspace_id: input.workspace_id, provider_key: key, name, service_kind: 'other', actor_user_id: input.actor_user_id, now,
        notes: `Provider pending: not in GAC Pro's provider catalog yet; GAC Pro has been asked to add it. Confirmed from a clue found by ${c.source_kind === 'repo_scan' ? 'the code scan' : 'discovery'}.`,
    });
    const changeId = providerRequestChangeId(key);
    const existing = (await db.query(`SELECT change_id FROM proposed_changes WHERE change_id = ? LIMIT 1`, [changeId])).results[0];
    if (!existing) {
        await new ProposedChangeService(db).create({
            change_id: changeId, target_type: 'research_source', change_type: 'create', source_kind: 'admin_edit', ai_confidence: 0,
            proposed_state: {
                kind: 'vendor_catalog', name: `Provider catalog request · ${name}`, url: providerRequestUrl(c, website), fetch_strategy: 'html', cadence: 'on_demand',
                description: `A customer confirmed they use ${name}, which is not in the provider catalog. Add it to connectors/providers.ts (reviewed code); this source is its public page.`,
                config: JSON.stringify({ provider_catalog_request: { provider_key: key, name } }),
            },
            ai_rationale: `Provider catalog request: a workspace owner or admin confirmed ${name} is used and is not in the provider catalog. The catalog is reviewed code (ADR-0051 §3): add the provider there. Approving this proposal also adds its public page as an on-demand vendor_catalog source; rejecting it blocklists the page.`,
        });
    }
    const reason = `${PROVIDER_PENDING_PREFIX}${name} · external service ${xs.external_service_id}${live ? '' : ' (new)'}`;
    const nowIso = now.toISOString();
    await db.execute(`UPDATE infra_unresolved_clues SET status = 'dismissed', dismiss_reason = ?, decided_by = ?, decided_at = ?, updated_at = ? WHERE clue_id = ? AND workspace_id = ? AND status = 'open'`, [reason, input.actor_user_id, nowIso, nowIso, c.clue_id, input.workspace_id]);
    return {
        clue: (await getUnresolvedClue(db, input.workspace_id, c.clue_id)), external_service_id: xs.external_service_id, external_service_created: !live,
        provider_key: key, catalog_request_id: changeId, catalog_request_created: !existing,
    };
}
export async function dismissUnresolvedClue(db, input) {
    assertPerson(input.actor_user_id);
    const reason = (input.reason ?? '').trim();
    if (!reason)
        fail('invalid', 'reason is required.');
    if (reason.length > 2000)
        fail('invalid', 'reason must be at most 2000 characters.');
    if (containsSecretOrEmail(reason))
        fail('invalid', 'reason looks like it contains a secret or an e-mail address; describe it instead.');
    const c = await openClue(db, input.workspace_id, input.clue_id);
    const nowIso = (input.now ?? new Date()).toISOString();
    await db.execute(`UPDATE infra_unresolved_clues SET status = 'dismissed', dismiss_reason = ?, decided_by = ?, decided_at = ?, updated_at = ? WHERE clue_id = ? AND workspace_id = ? AND status = 'open'`, [reason, input.actor_user_id, nowIso, nowIso, c.clue_id, input.workspace_id]);
    return (await getUnresolvedClue(db, input.workspace_id, c.clue_id));
}
//# sourceMappingURL=infra-unresolved-clue-service.js.map